import { useState, useEffect, useRef } from 'react';
import { ChevronsUpDown, Search } from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { useInboundShipmentItemsStore } from '@/stores/inbound-shipment-items-store';
import { useToastStore } from '@/stores/toast-store';
// import { shipmentItemApi } from '@/lib/api';

interface SplitTableShipmentItem {
  id: string;
  identifier: string;
  orderDate: string;
  zone: string;
  marketplace: string;
  productName: string;
  quantity: number;
  weight: string;
  sku: string;
}

function CustomCheckbox({ 
  checked, 
  onChange, 
  indeterminate = false 
}: { 
  checked: boolean; 
  onChange: (checked: boolean) => void;
  indeterminate?: boolean;
}) {
  return (
    <div className="relative">
      <input 
        type="checkbox" 
        checked={checked}
        ref={(el) => {
          if (el) el.indeterminate = indeterminate;
        }}
        onChange={(e) => onChange(e.target.checked)}
        className="peer w-4 h-4 rounded border-2 border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-2 bg-white appearance-none checked:bg-blue-600 checked:border-blue-600" 
      />
      <svg 
        className="absolute inset-0 w-4 h-4 pointer-events-none opacity-0 peer-checked:opacity-100 text-white" 
        fill="none" 
        viewBox="0 0 24 24" 
        stroke="currentColor"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    </div>
  );
}

const SearchInput = ({ placeholder, value, onChange }: { placeholder: string; value: string; onChange: (value: string) => void }) => (
  <div className="relative">
    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
    <input
      type="text"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-full border border-[#D7DEEC] bg-white py-2 pl-10 pr-4 text-sm text-[#34415F] focus:border-[#C7D0E5] focus:outline-none focus:ring-2 focus:ring-[#E8EDF8]"
    />
  </div>
);

const TableHeader = ({ 
  // isLeftTable, 
  selectedItems, 
  totalItems, 
  onSelectAll 
}: { 
  isLeftTable: boolean;
  selectedItems: Set<string>;
  totalItems: number;
  onSelectAll: (checked: boolean) => void;
}) => {
  const isAllSelected = totalItems > 0 && selectedItems.size === totalItems;
  const isIndeterminate = selectedItems.size > 0 && selectedItems.size < totalItems;

  return (
    <thead>
      <tr className="bg-[#EDF1FF] border-b border-gray-200">
        <th className="py-3 px-4 text-left border-r border-gray-200">
          <CustomCheckbox 
            checked={isAllSelected}
            onChange={onSelectAll}
            indeterminate={isIndeterminate}
          />
        </th>
        <th className="py-3 px-4 text-left text-[12px] font-medium text-[#5E6784] border-r border-[#E7EBF3]">
          <div className="flex items-center gap-1">
            Identifier
            <ChevronsUpDown size={14} className="text-gray-400" />
          </div>
        </th>
        <th className="py-3 px-4 text-left text-[12px] font-medium text-[#5E6784] border-r border-[#E7EBF3]">
          <div className="flex items-center gap-1">
            Order Date
            <ChevronsUpDown size={14} className="text-gray-400" />
          </div>
        </th>
        <th className="py-3 px-4 text-left text-[12px] font-medium text-[#5E6784]">
          <div className="flex items-center gap-1">
            MKT
            <ChevronsUpDown size={14} className="text-gray-400" />
          </div>
        </th>
      </tr>
    </thead>
  );
};

export default function SplitShipmentItemsTable({ 
  shipmentId,
  storeId,
  shipmentReference,
  masterWayBill,
  // onShipmentSubmitClick
}: { 
  shipmentId: number;
  storeId: number;
  shipmentReference?: string;
  masterWayBill?: string;
  onShipmentSubmitClick?: () => void;
}) {
  const { token } = useAuthStore();
  const { items: shipmentItems, isLoading, error, fetchItems } = useInboundShipmentItemsStore();
  const { addToast } = useToastStore();
  
  // State for split table (same as SplitOrdersTable)
  const [leftTableOrders, setLeftTableOrders] = useState<SplitTableShipmentItem[]>([]);
  const [rightTableOrders, setRightTableOrders] = useState<SplitTableShipmentItem[]>([]);
  const [selectedLeftItems, setSelectedLeftItems] = useState<Set<string>>(new Set());
  const [selectedRightItems, setSelectedRightItems] = useState<Set<string>>(new Set());
  const [, setIsDragOver] = useState(false);
  const [, setDragOverTable] = useState<'left' | 'right' | null>(null);
  
  // State for search (same as SplitOrdersTable)
  const [searchTerm, setSearchTerm] = useState('');
  
  // Ref to track current right table orders for immediate access
  const rightTableOrdersRef = useRef<SplitTableShipmentItem[]>([]);
  


  // Emit event when right table items change
  useEffect(() => {
    console.log('📊 rightTableOrders changed:', rightTableOrders);
    console.log('📊 rightTableOrders length:', rightTableOrders.length);
    
    // Update ref with current state
    rightTableOrdersRef.current = rightTableOrders;
    
    const event = new CustomEvent('shipment-items-selected', {
      detail: { hasItems: rightTableOrders.length > 0, itemCount: rightTableOrders.length, items: rightTableOrders }
    });
    window.dispatchEvent(event);
  }, [rightTableOrders.length, rightTableOrders]);

  // Listen for shipment submit button clicks
  useEffect(() => {
    const handleShipmentSubmit = (event: CustomEvent) => {
      if (event.detail?.action === 'submit') {
        handleSubmit();
      }
    };

    window.addEventListener('shipment-submit-clicked', handleShipmentSubmit as EventListener);
    
    return () => {
      window.removeEventListener('shipment-submit-clicked', handleShipmentSubmit as EventListener);
    };
  }, []);

  // Listen for successful submission to clear right table
  useEffect(() => {
    const handleSubmissionSuccess = (event: CustomEvent) => {
      if (event.detail?.action === 'success') {
        // Clear the right table
        setRightTableOrders([]);
        setSelectedRightItems(new Set());
        
        // Show success toast
        addToast({
          type: 'success',
          title: 'Success!',
          message: 'Items submitted successfully.',
          duration: 4000
        });
      }
    };

    const handleSubmissionError = (event: CustomEvent) => {
      if (event.detail?.action === 'error') {
        // Show error toast
        addToast({
          type: 'error',
          title: 'Submission Failed',
          message: event.detail?.message || 'Failed to submit items. Please try again.',
          duration: 5000
        });
      }
    };

    window.addEventListener('shipment-submit-success', handleSubmissionSuccess as EventListener);
    window.addEventListener('shipment-submit-error', handleSubmissionError as EventListener);
    
    return () => {
      window.removeEventListener('shipment-submit-success', handleSubmissionSuccess as EventListener);
      window.removeEventListener('shipment-submit-error', handleSubmissionError as EventListener);
    };
  }, [addToast]);


  // Fetch shipment items
  useEffect(() => {
    if (!token || !storeId || !shipmentId) return;
    fetchItems({ token, storeId, shipmentId, shipmentReference, masterWayBill });
  }, [token, storeId, shipmentId, shipmentReference, masterWayBill, fetchItems]);

  // Transform shipment items to split table format (same structure as SplitOrdersTable)
  useEffect(() => {
    if (shipmentItems.length > 0) {
      const transformedItems: SplitTableShipmentItem[] = shipmentItems.map((item) => ({
        id: String(item.id),
        identifier: item.code || 'N/A',
        orderDate: new Date(item.created_at || item.shipment_created_at || '').toLocaleDateString('en-US', { 
          day: '2-digit', 
          month: 'short', 
          year: 'numeric' 
        }),
        zone: item.marketplace_name || 'N/A',
        marketplace: item.marketplace_name || 'N/A',
        productName: item.marketplace_product_name || 'N/A',
        quantity: item.qty || 0,
        weight: item.weight || 'N/A',
        sku: item.sku || 'N/A'
      }));
      setLeftTableOrders(transformedItems);
    }
  }, [shipmentItems]);

  // Filter items based on search term (same logic as SplitOrdersTable)
  const filteredLeftItems = leftTableOrders.filter(item => 
    item.identifier.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.sku.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredRightItems = rightTableOrders.filter(item => 
    item.identifier.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.sku.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleLeftItemToggle = (itemId: string) => {
    const newSelected = new Set(selectedLeftItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedLeftItems(newSelected);
  };

  const handleRightItemToggle = (itemId: string) => {
    const newSelected = new Set(selectedRightItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedRightItems(newSelected);
  };

  const handleLeftSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set(filteredLeftItems.map(item => item.id));
      setSelectedLeftItems(allIds);
    } else {
      setSelectedLeftItems(new Set());
    }
  };

  const handleRightSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set(filteredRightItems.map(item => item.id));
      setSelectedRightItems(allIds);
    } else {
      setSelectedRightItems(new Set());
    }
  };

  const handleMoveToRight = () => {
    const selectedItems = filteredLeftItems.filter(item => selectedLeftItems.has(item.id));
    console.log('🔄 handleMoveToRight - selectedItems:', selectedItems);
    console.log('🔄 handleMoveToRight - current rightTableOrders:', rightTableOrders);
    
    setRightTableOrders(prev => {
      const newRightTable = [...prev, ...selectedItems];
      console.log('🔄 handleMoveToRight - new rightTableOrders:', newRightTable);
      return newRightTable;
    });
    
    setLeftTableOrders(prev => {
      const newLeftTable = prev.filter(item => !selectedLeftItems.has(item.id));
      console.log('🔄 handleMoveToRight - new leftTableOrders:', newLeftTable);
      return newLeftTable;
    });
    
    setSelectedLeftItems(new Set());
    
    // Emit event to notify that items are in the right table
    const event = new CustomEvent('shipment-items-selected', {
      detail: { hasItems: true, itemCount: rightTableOrders.length + selectedItems.length }
    });
    window.dispatchEvent(event);
  };

  const handleMoveToLeft = () => {
    const selectedItems = filteredRightItems.filter(item => selectedRightItems.has(item.id));
    setLeftTableOrders(prev => [...prev, ...selectedItems]);
    setRightTableOrders(prev => prev.filter(item => !selectedRightItems.has(item.id)));
    setSelectedRightItems(new Set());
    
    // Emit event to notify that items are removed from the right table
    const event = new CustomEvent('shipment-items-selected', {
      detail: { hasItems: rightTableOrders.length - selectedItems.length > 0, itemCount: rightTableOrders.length - selectedItems.length }
    });
    window.dispatchEvent(event);
  };

  const handleSubmit = () => {
    console.log('🚀 SplitShipmentItemsTable.handleSubmit called!');
    console.log('Timestamp:', new Date().toISOString());
    console.log('isLoading:', isLoading);
    console.log('error:', error);
    console.log('shipmentItems from store (original API data):', shipmentItems);
    console.log('shipmentItems length:', shipmentItems.length);
    console.log('rightTableOrders (state):', rightTableOrders);
    console.log('rightTableOrders length (state):', rightTableOrders.length);
    console.log('rightTableOrdersRef.current:', rightTableOrdersRef.current);
    console.log('rightTableOrdersRef.current length:', rightTableOrdersRef.current.length);
    console.log('filteredRightItems:', filteredRightItems);
    console.log('filteredRightItems length:', filteredRightItems.length);
    
    if (isLoading) {
      addToast({
        type: 'warning',
        title: 'Loading...',
        message: 'Please wait while shipment data is loading...',
        duration: 3000
      });
      return;
    }
    
    if (error) {
      addToast({
        type: 'error',
        title: 'Error',
        message: `Error loading shipment data: ${error}. Please refresh and try again.`,
        duration: 5000
      });
      return;
    }
    
    // Check both state and ref for right table items
    const hasRightTableItems = rightTableOrders.length > 0 || rightTableOrdersRef.current.length > 0 || filteredRightItems.length > 0;
    console.log('hasRightTableItems:', hasRightTableItems);
    
    if (!hasRightTableItems) {
      addToast({
        type: 'warning',
        title: 'No Items Selected',
        message: 'Please select at least one item to submit.',
        duration: 3000
      });
      return;
    }
    
    console.log('🚀 Fresh store state:', { items: shipmentItems, isLoading, error, fetchItems });
    console.log('🚀 Fresh store items length:', shipmentItems.length);
    
    // Use the actual items that should be submitted (prioritize ref, then state, then filtered)
    const itemsToSubmit = rightTableOrdersRef.current.length > 0 
      ? rightTableOrdersRef.current 
      : rightTableOrders.length > 0 
        ? rightTableOrders 
        : filteredRightItems;
    console.log('itemsToSubmit:', itemsToSubmit);
    
    // If shipmentItems is empty, we can still proceed with the items in the right table
    // The LayoutWrapper will handle the submission with the available data
    const originalShipmentItems = shipmentItems.length > 0 ? shipmentItems : [];
    console.log('originalShipmentItems to send:', originalShipmentItems);
    
    // Dispatch event to notify LayoutWrapper to show confirmation dialog
    const event = new CustomEvent('shipment-submit-confirm', {
      detail: { 
        action: 'confirm',
        items: itemsToSubmit,
        originalShipmentItems: originalShipmentItems
      }
    });
    window.dispatchEvent(event);
  };

  const handleLeftTableDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
    setDragOverTable('left');
  };

  const handleRightTableDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
    setDragOverTable('right');
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
    setDragOverTable(null);
  };

  const handleLeftTableDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const draggedItemId = e.dataTransfer.getData('text/plain');
    if (draggedItemId) {
      // Find the item in the right table and move it to the left
      const itemToMove = rightTableOrders.find(item => item.id === draggedItemId);
      if (itemToMove) {
        setRightTableOrders(prev => prev.filter(item => item.id !== draggedItemId));
        setLeftTableOrders(prev => [...prev, itemToMove]);
        
        // Emit event to notify that items are removed from the right table
        const event = new CustomEvent('shipment-items-selected', {
          detail: { hasItems: rightTableOrders.length - 1 > 0, itemCount: rightTableOrders.length - 1, items: rightTableOrders.filter(item => item.id !== draggedItemId) }
        });
        window.dispatchEvent(event);
      }
    }
  };

  const handleRightTableDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const draggedItemId = e.dataTransfer.getData('text/plain');
    if (draggedItemId) {
      // Find the item in the left table and move it to the right
      const itemToMove = leftTableOrders.find(item => item.id === draggedItemId);
      if (itemToMove) {
        setLeftTableOrders(prev => prev.filter(item => item.id !== draggedItemId));
        setRightTableOrders(prev => [...prev, itemToMove]);
        
        // Emit event to notify that items are added to the right table
        const event = new CustomEvent('shipment-items-selected', {
          detail: { hasItems: true, itemCount: rightTableOrders.length + 1, items: [...rightTableOrders, itemToMove] }
        });
        window.dispatchEvent(event);
      }
    }
  };

  const LeftTableBody = () => (
    <tbody className="divide-y divide-gray-200">
      {filteredLeftItems.map((item) => (
        <tr 
          key={item.id} 
          className="hover:bg-gray-50 cursor-move"
          draggable
          onDragStart={(e) => {
            e.dataTransfer.setData('text/plain', item.id);
            e.dataTransfer.effectAllowed = 'move';
            e.currentTarget.style.opacity = '0.5';
          }}
          onDragEnd={(e) => {
            e.currentTarget.style.opacity = '1';
          }}
        >
          <td className="py-3 px-4 border-r border-[#EEF1F6]">
            <CustomCheckbox 
              checked={selectedLeftItems.has(item.id)}
              onChange={() => handleLeftItemToggle(item.id)}
            />
          </td>
          <td className="py-3 px-4 text-sm text-[#34415F] border-r border-[#EEF1F6]">{item.identifier}</td>
          <td className="py-3 px-4 text-sm text-[#34415F] border-r border-[#EEF1F6]">{item.orderDate}</td>
          <td className="py-3 px-4 text-sm text-[#34415F]">{item.zone}</td>
        </tr>
      ))}
    </tbody>
  );

  const RightTableBody = () => (
    <tbody className="divide-y divide-gray-200">
      {filteredRightItems.map((item) => (
        <tr 
          key={item.id} 
          className="hover:bg-gray-50 cursor-move"
          draggable
          onDragStart={(e) => {
            e.dataTransfer.setData('text/plain', item.id);
            e.dataTransfer.effectAllowed = 'move';
            e.currentTarget.style.opacity = '0.5';
          }}
          onDragEnd={(e) => {
            e.currentTarget.style.opacity = '1';
          }}
        >
          <td className="py-3 px-4 border-r border-[#EEF1F6]">
            <CustomCheckbox 
              checked={selectedRightItems.has(item.id)}
              onChange={() => handleRightItemToggle(item.id)}
            />
          </td>
          <td className="py-3 px-4 text-sm text-[#34415F] border-r border-[#EEF1F6]">{item.identifier}</td>
          <td className="py-3 px-4 text-sm text-[#34415F] border-r border-[#EEF1F6]">{item.orderDate}</td>
          <td className="py-3 px-4 text-sm text-[#34415F]">{item.zone}</td>
        </tr>
      ))}
    </tbody>
  );

  const Footer = (count: number) => (
    <div className="flex flex-col gap-3 border-t border-[#E7EBF3] bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-2 text-sm text-[#5E6784]">
        <span>Show</span>
        <select className="rounded-md border border-[#D7DEEC] bg-white px-2 py-1 text-sm text-[#34415F] focus:outline-none focus:ring-2 focus:ring-[#E8EDF8]">
          <option value={5}>5</option>
          <option value={10}>10</option>
          <option value={20}>20</option>
        </select>
        <span>per page</span>
      </div>
      <div className="flex items-center gap-4 text-sm text-[#5E6784]">
        <span>{count === 0 ? '0-0' : `1-${count}`} of {count}</span>
        <div className="flex items-center gap-1">
          <button className="rounded p-1 hover:bg-gray-100">
            <span className="sr-only">Previous</span>
            &larr;
          </button>
          {[1, 2, 3, 4, 5].map((pageNum) => (
            <button
              key={pageNum}
              className={`px-2.5 py-1 text-sm ${pageNum === 2 ? 'rounded-md bg-[#F1F1F4] text-[#24304F]' : 'text-[#5E6784] hover:bg-gray-100'}`}
            >
              {pageNum}
            </button>
          ))}
          <button className="rounded p-1 hover:bg-gray-100">
            <span className="sr-only">Next</span>
            &rarr;
          </button>
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 mx-16 p-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-2 text-gray-600">Loading shipment items...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 mx-16 p-8">
        <div className="text-center text-sm text-red-600">{error}</div>
      </div>
    );
  }

    return (
    <div className="mx-16 grid grid-cols-1 gap-6 xl:grid-cols-2" data-component="SplitShipmentItemsTable">
             {/* Left Table - Available Items */}
       <div 
         className="rounded-[20px] border border-[#E6EAF1] bg-[#F1F1F4] p-4 shadow-sm"
         onDragOver={handleLeftTableDragOver}
         onDragLeave={handleDragLeave}
         onDrop={handleLeftTableDrop}
       >
           <div className="mb-4 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
             <div className="w-full sm:w-[190px]">
               <SearchInput 
                 placeholder="Search for Items" 
                 value={searchTerm}
                 onChange={setSearchTerm}
               />
             </div>
             <button 
               onClick={handleMoveToRight}
               disabled={selectedLeftItems.size === 0}
               className="h-10 min-w-[112px] rounded-lg bg-[#FFC107] px-6 text-sm font-semibold text-[#1F2432] transition-colors hover:bg-[#F2B400] disabled:cursor-not-allowed disabled:bg-gray-300"
             >
               Add
             </button>
           </div>
         <div className="overflow-hidden rounded-[18px] bg-white">
           <div className="overflow-x-auto">
           <table className="min-w-full">
             <TableHeader 
               isLeftTable={true} 
               selectedItems={selectedLeftItems}
               totalItems={filteredLeftItems.length}
               onSelectAll={handleLeftSelectAll}
             />
             <LeftTableBody />
           </table>
           </div>
           {Footer(filteredLeftItems.length)}
         </div>
      </div>

             {/* Right Table - Selected Items */}
       <div 
         className="rounded-[20px] border border-[#E6EAF1] bg-[#F1F1F4] p-4 shadow-sm"
         onDragOver={handleRightTableDragOver}
         onDragLeave={handleDragLeave}
         onDrop={handleRightTableDrop}
       >
          <div className="mb-4 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
            <div className="w-full sm:w-[190px]">
              <SearchInput 
                placeholder="Search for Items" 
                value={searchTerm}
                onChange={setSearchTerm}
              />
            </div>
            <button 
              onClick={handleMoveToLeft}
              disabled={selectedRightItems.size === 0}
              className="h-10 min-w-[112px] rounded-lg bg-[#FF2D75] px-6 text-sm font-semibold text-white transition-colors hover:bg-[#E82669] disabled:cursor-not-allowed disabled:bg-gray-300"
            >
              Remove
            </button>
          </div>
        <div className="overflow-hidden rounded-[18px] bg-white">
          <div className="overflow-x-auto">
           <table className="min-w-full">
             <TableHeader 
               isLeftTable={false} 
               selectedItems={selectedRightItems}
               totalItems={filteredRightItems.length}
               onSelectAll={handleRightSelectAll}
             />
             <RightTableBody />
           </table>
          </div>
          {Footer(filteredRightItems.length)}
        </div>
      </div>
    </div>
  );
}
